import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";
import { imageUploadApi } from "../../services/api";

const STEP5_LS_KEY = "glp1_intake_step5";
const LS_KEY = "glp1_intake_step5a";

// ==============================
// Step 5 (NEW FORMAT) – question -> full answer
// ==============================
const STEP5_QUESTION_KEY =
  "Q5_Have_you_taken_medication_for_weight_loss_within_the_past_4_weeks";

const STEP5_ANSWERS = {
  GLP1: "Yes, I've taken GLP-1 medication",
  OTHER: "Yes, I've taken a different medication for weight loss",
  NO: "No",
} as const;

type Step5Answer = (typeof STEP5_ANSWERS)[keyof typeof STEP5_ANSWERS] | "";

// We still keep a small internal choice only for branching UI
type Step5Choice = "" | "glp1" | "other" | "no";

const mapStep5AnswerToChoice = (ans: Step5Answer): Step5Choice => {
  if (ans === STEP5_ANSWERS.GLP1) return "glp1";
  if (ans === STEP5_ANSWERS.OTHER) return "other";
  if (ans === STEP5_ANSWERS.NO) return "no";
  return "";
};

const isStep5Answer = (v: unknown): v is Exclude<Step5Answer, ""> => {
  return typeof v === "string" && (Object.values(STEP5_ANSWERS) as readonly string[]).includes(v);
};

const readStep5ChoiceFromLS = (): Step5Choice => {
  try {
    const raw = localStorage.getItem(STEP5_LS_KEY);
    if (!raw) return "";

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const val = parsed?.[STEP5_QUESTION_KEY];

    if (!isStep5Answer(val)) return "";
    return mapStep5AnswerToChoice(val);
  } catch {
    return "";
  }
};

// ==============================
// Step 5A (NEW FORMAT) – question -> full answer
// ==============================
const Q5A_KEYS = {
  MEDS_TEXT:
    "Q5A_Please_list_the_name_dose_and_frequency_of_your_weight_loss_medication",
  LAST_DOSE: "Q5A_When_was_your_last_dose_of_medication",
  STARTING_WEIGHT: "Q5A_What_was_your_starting_weight_in_pounds_lbs",
  UPLOAD_URL: "Q5A_patient_image_url",
  AGREE_ONLY_PROGRAM:
    "Q5A_Do_you_agree_to_only_obtain_weight_loss_medication_through_this_program_moving_forward",
} as const;

// full answer strings only (no "0-5" etc)
const LAST_DOSE_ANSWERS = {
  D0_5: "0-5 days",
  D6_10: "6-10 days",
  D11_14: "11-14 days",
  W2_4: "More than 2 weeks ago but within the last 4 weeks",
  W4P: "More than 4 weeks ago",
} as const;

type LastDoseAnswer =
  (typeof LAST_DOSE_ANSWERS)[keyof typeof LAST_DOSE_ANSWERS] | "";

type UploadState = {
  name: string;
  type: string;
  size: number;
  url: string; // server url
};

type FormState = {
  medsText: string;
  startingWeight: string;
  agreeOnlyProgram: "" | "Yes" | "No";
  lastDose: LastDoseAnswer; // FULL answer
  upload?: UploadState | null;
};

const DEFAULT: FormState = {
  medsText: "",
  startingWeight: "",
  agreeOnlyProgram: "",
  lastDose: "",
  upload: null,
};

type OptionCardProps = {
  type: "radio" | "checkbox";
  name?: string;
  label: string;
  checked: boolean;
  onChange: () => void;
};

const OptionCard: React.FC<OptionCardProps> = ({
  type,
  name,
  label,
  checked,
  onChange,
}) => {
  return (
    <button
      type="button"
      className={`funnel-option funnel-checkbox ${checked ? "is-selected" : ""}`}
      onClick={onChange}
    >
      <span className={`funnel-control ${type} ${checked ? "is-selected" : ""}`}>
        {type === "radio" ? <span className="dot" /> : <span className="tick" />}
      </span>

      <span className="funnel-option__label">{label}</span>

      <input className="sr-only" type={type} name={name} checked={checked} onChange={onChange} />
    </button>
  );
};

const isPositiveNumber = (v: string) => /^\d+$/.test(v) && Number(v) > 0;

// read Step5A as question->answer object, and map into FormState
const readStep5AFromLS = (): FormState => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT;

    const parsed = JSON.parse(raw) as Record<string, unknown>;

    const medsText = typeof parsed?.[Q5A_KEYS.MEDS_TEXT] === "string" ? String(parsed[Q5A_KEYS.MEDS_TEXT]) : "";
    const startingWeight =
      typeof parsed?.[Q5A_KEYS.STARTING_WEIGHT] === "string" ? String(parsed[Q5A_KEYS.STARTING_WEIGHT]) : "";

    const agreeOnlyProgram =
      parsed?.[Q5A_KEYS.AGREE_ONLY_PROGRAM] === "Yes" || parsed?.[Q5A_KEYS.AGREE_ONLY_PROGRAM] === "No"
        ? (parsed[Q5A_KEYS.AGREE_ONLY_PROGRAM] as "Yes" | "No")
        : "";

    const lastDose =
      typeof parsed?.[Q5A_KEYS.LAST_DOSE] === "string" &&
      (Object.values(LAST_DOSE_ANSWERS) as readonly string[]).includes(String(parsed[Q5A_KEYS.LAST_DOSE]))
        ? (parsed[Q5A_KEYS.LAST_DOSE] as LastDoseAnswer)
        : "";

    const uploadUrl = typeof parsed?.[Q5A_KEYS.UPLOAD_URL] === "string" ? String(parsed[Q5A_KEYS.UPLOAD_URL]) : "";
    const uploadMeta = parsed?.["__uploadMeta"];

    const upload =
      uploadUrl && uploadMeta && typeof uploadMeta === "object"
        ? {
            name: String((uploadMeta as any).name || ""),
            type: String((uploadMeta as any).type || ""),
            size: Number((uploadMeta as any).size || 0),
            url: uploadUrl,
          }
        : uploadUrl
        ? { name: "Uploaded file", type: "", size: 0, url: uploadUrl }
        : null;

    return {
      medsText,
      startingWeight,
      agreeOnlyProgram,
      lastDose,
      upload,
    };
  } catch {
    return DEFAULT;
  }
};

// build the exact payload stored in LS (question -> answer)
const buildStep5APayload = (form: FormState) => {
  const payload: Record<string, any> = {
    [Q5A_KEYS.MEDS_TEXT]: form.medsText.trim(),
    [Q5A_KEYS.STARTING_WEIGHT]: form.startingWeight.trim(),
    [Q5A_KEYS.AGREE_ONLY_PROGRAM]: form.agreeOnlyProgram,
  };

  // only store lastDose + upload when GLP1 flow (caller can decide),
  // but we keep them if present to preserve data
  if (form.lastDose) payload[Q5A_KEYS.LAST_DOSE] = form.lastDose;
  if (form.upload?.url) payload[Q5A_KEYS.UPLOAD_URL] = form.upload.url;

  // optional meta (nice for UI showing filename)
  if (form.upload?.url) {
    payload["__uploadMeta"] = {
      name: form.upload.name,
      type: form.upload.type,
      size: form.upload.size,
    };
  }

  return payload;
};

const NewFunnelStep5A: React.FC = () => {
  const navigate = useNavigate();

  const [choice, setChoice] = useState<Step5Choice>("");
  const [form, setForm] = useState<FormState>(DEFAULT);
  const [touched, setTouched] = useState(false);

  const [hydrated, setHydrated] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [imageError, setImageError] = useState<string>("");
  const [uploading, setUploading] = useState(false);

  const openFilePicker = () => fileInputRef.current?.click();

  // ✅ HYDRATE: Step5 choice + Step5A data
  useEffect(() => {
    const c = readStep5ChoiceFromLS();
    setChoice(c);

    const saved = readStep5AFromLS();
    setForm(saved);

    setHydrated(true);
  }, []);

  // ✅ SAVE Step5A in question->answer format (after hydration)
  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(LS_KEY, JSON.stringify(buildStep5APayload(form)));
  }, [form, hydrated]);

  // ✅ redirect if user shouldn't be here
  useEffect(() => {
    if (!hydrated) return;

    if (choice === "no") {
      navigate("/glp1/intake-form/step-5", { replace: true });
      return;
    }

    // if someone landed here directly without step5 answer, push back
    if (choice === "") {
      navigate("/glp1/intake-form/step-3", { replace: true });
    }
  }, [choice, hydrated, navigate]);

  const isGlp1 = choice === "glp1";
  const isOther = choice === "other";

  const heading = useMemo(() => {
    if (isGlp1) return "Great! You have experience with GLP-1.";
    if (isOther) return "Great! You have experience with weight loss medication.";
    return "";
  }, [isGlp1, isOther]);

  const medsPrompt = useMemo(() => {
    if (isGlp1) return "Please list the name, dose, and frequency of your GLP-1 medication.";
    if (isOther) return "Please list the name, dose, and frequency of your weight loss medication.";
    return "";
  }, [isGlp1, isOther]);

  const validate = () => {
    if (!form.medsText.trim()) return false;
    if (!form.startingWeight.trim() || !isPositiveNumber(form.startingWeight)) return false;
    if (!form.agreeOnlyProgram) return false;
    if (isGlp1 && !form.lastDose) return false;
    return true;
  };

  const onNext = () => {
    setTouched(true);
    if (!validate()) return;
    navigate("/glp1/intake-form/step-5");
  };

  const onBack = () => navigate("/glp1/intake-form/step-4");

  const showTextError = touched && !form.medsText.trim();
  const showWeightRequiredError = touched && !form.startingWeight.trim();
  const showWeightInvalidError = touched && !!form.startingWeight.trim() && !isPositiveNumber(form.startingWeight);
  const showLastDoseError = touched && isGlp1 && !form.lastDose;
  const showAgreeError = touched && !form.agreeOnlyProgram;

  const handleFilePick = async (file: File | null) => {
    setImageError("");

    if (!file) {
      setForm((p) => ({ ...p, upload: null }));
      return;
    }

    try {
      setUploading(true);
      setForm((p) => ({ ...p, upload: null }));

      const response = await imageUploadApi(file);

      const nextUpload: UploadState = {
        name: file.name,
        size: file.size,
        type: file.type,
        url: response.image_url,
      };

      setForm((p) => ({ ...p, upload: nextUpload }));
    } catch (error) {
      console.error("Image upload failed:", error);
      setImageError("Image upload failed. Try again");
      setForm((p) => ({ ...p, upload: null }));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await handleFilePick(file);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  if (!choice || (!isGlp1 && !isOther)) {
    return (
      <FunnelLayout>
        <div className="funnel-section">
          <div className="funnel-q-title">Loading...</div>
        </div>
      </FunnelLayout>
    );
  }

  return (
    <FunnelLayout>
      <div className="funnel-section">
        <div className="funnel-q-title funnel-q-title-heading">{heading}</div>

        <div className="funnel-q-title" style={{ marginTop: "12px" }}>
          {medsPrompt}
        </div>

        <textarea
          className="funnel-input"
          value={form.medsText}
          onChange={(e) => setForm((p) => ({ ...p, medsText: e.target.value }))}
          rows={6}
        />
        {showTextError && <div className="funnel-error">Field is required</div>}

        {isGlp1 && (
          <>
            <div className="funnel-q-title" style={{ marginTop: "20px", marginBottom: "16px" }}>
              When was your last dose of medication?
            </div>

            {Object.values(LAST_DOSE_ANSWERS).map((label) => (
              <OptionCard
                key={label}
                type="radio"
                name="lastDose"
                label={label}
                checked={form.lastDose === label}
                onChange={() => setForm((p) => ({ ...p, lastDose: label }))}
              />
            ))}

            {showLastDoseError && <div className="funnel-error">Please select one option.</div>}
          </>
        )}

        <div className="funnel-q-title" style={{ marginTop: "20px", marginBottom: "16px" }}>
          What was your starting weight in pounds?
        </div>

        <input
          className="funnel-input"
          inputMode="numeric"
          placeholder="lbs"
          value={form.startingWeight}
          onChange={(e) =>
            setForm((p) => ({
              ...p,
              startingWeight: e.target.value.replace(/\D/g, ""),
            }))
          }
        />

        {showWeightRequiredError && <div className="funnel-error">Field is required</div>}
        {!showWeightRequiredError && showWeightInvalidError && (
          <div className="funnel-error">Please enter a valid number</div>
        )}

        {isGlp1 && (
          <>
            <div className="funnel-q-title" style={{ marginTop: "20px", marginBottom: "16px" }}>
              Please take or upload a photo of your GLP-1 medication
            </div>

            <div className="funnel-upload-subtext">
              If you are requesting a prescription for your current or higher dose, this is important. If you don&apos;t
              have a photo available, you can skip this.
            </div>

            <div
              className={`funnel-dropzone ${isDragging ? "is-dragging" : ""}`}
              role="button"
              tabIndex={0}
              onClick={openFilePicker}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") openFilePicker();
              }}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
            >
              <div className="funnel-dropzone__inner">
                <div className="funnel-dropzone__icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"></path>
                  </svg>
                </div>

                <div className="funnel-dropzone__text">
                  Drag &amp; drop a file or <span className="funnel-dropzone__browse">browse</span>
                </div>

                {uploading && <div className="funnel-dropzone__file">Uploading...</div>}

                {!uploading && form.upload?.name && (
                  <div className="funnel-dropzone__file">
                    Uploaded: <strong>{form.upload.name}</strong>
                  </div>
                )}

                {!uploading && form.upload?.url && (
                  <div className="funnel-dropzone__file" style={{ opacity: 0.7 }}>
                    URL saved
                  </div>
                )}

                {imageError && <div className="funnel-error">{imageError}</div>}
              </div>

              <input
                ref={fileInputRef}
                className="sr-only"
                type="file"
                accept="image/*"
                onChange={(e) => handleFilePick(e.target.files?.[0] || null)}
              />
            </div>
          </>
        )}

        <div className="funnel-q-title" style={{ marginTop: "20px", marginBottom: "10px" }}>
          Do you agree to only obtain weight loss medication through this program moving forward?
        </div>

        <div className="funnel-option__label" style={{ opacity: 0.6, marginBottom: "16px" }}>
          It&apos;s important not to &quot;stack&quot; weight loss medications
        </div>

        <OptionCard
          type="radio"
          name="agreeOnlyProgram"
          label="Yes"
          checked={form.agreeOnlyProgram === "Yes"}
          onChange={() => setForm((p) => ({ ...p, agreeOnlyProgram: "Yes" }))}
        />
        <OptionCard
          type="radio"
          name="agreeOnlyProgram"
          label="No"
          checked={form.agreeOnlyProgram === "No"}
          onChange={() => setForm((p) => ({ ...p, agreeOnlyProgram: "No" }))}
        />

        {showAgreeError && <div className="funnel-error">Field is required</div>}
      </div>

      <button className="next-btn" onClick={onNext} disabled={!validate()}>
        Next →
      </button>

      <button type="button" className="back-btn" onClick={onBack} style={{ display: "none" }}>
        Back
      </button>
    </FunnelLayout>
  );
};

export default NewFunnelStep5A;